import { FC, useEffect, useMemo, useRef } from "react";
import {
  AccordionLayout,
  FormikSelectField,
  FormikInputField,
  Modal,
  types,
  FormikCheckbox
} from "@vilocnv/allsetra-core";
import {
  Box,
  Divider,
  ModalProps,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import { useFormikContext } from "formik";
import { getUserRole, OBJECT_RIDE_MODES_OPTIONS } from "app/data/helpers";
import {
  getAccountPointsOfInterestsThunk,
  getAllKeysByAccountThunk,
  getAllUserRolesThunk,
  getGooglePlacesThunk,
  getSpecificAccountUserThunk,
  getSpecificSettingThunk,
  getUsersBasicInfoThunk
} from "app/features";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  selectAccountKeysState,
  selectAccountsState,
  selectDrawerSelectedAccountId,
  selectSettingsState,
  selectUserRolesState
} from "app/data/selectors";
import { useTranslation } from "react-i18next";

type Props = Omit<ModalProps, "title" | "children"> & {
  customIsSubmitting: boolean;
  objectRide: types.IObjectRide | null;
};

const ALLOWED_ROLES = ["Super Admin", "Admin", "Manager"];

const InnerForm: FC<Props> = ({
  open,
  onClose,
  customIsSubmitting,
  objectRide,
  ...rest
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { accountUser } = useAppSelector(selectAccountsState);
  const { specificSetting } = useAppSelector(selectSettingsState);
  const { userRoles } = useAppSelector(selectUserRolesState);

  // @ts-ignore
  const accountRole = getUserRole(accountUser?.role, userRoles);

  const {
    resetForm,
    isSubmitting,
    isValid,
    handleSubmit,
    setFieldValue,
    values,
    initialValues
  } = useFormikContext<any>();

  // Global State
  const accountId = useAppSelector(selectDrawerSelectedAccountId);

  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );
  const { usersBasicInfo } = useAppSelector(selectAccountsState);
  const { t } = useTranslation(["translation"]);

  const rideModes = useMemo(() => {
    const baseOptions = OBJECT_RIDE_MODES_OPTIONS(t);
    // @ts-ignore
    const privateMileageValue = values.privateMileage;
    const privateMileageNumber = privateMileageValue
      ? Number(privateMileageValue)
      : 0;

    if (privateMileageNumber > 0) {
      return [...baseOptions, { value: 4, label: t("objectRideModes.4") }];
    }
    return baseOptions;
    // @ts-ignore
  }, [t, values.privateMileage]);

  const hasChanges = useMemo(() => {
    if (!initialValues) return false;

    const fieldsToCompare = [
      "keyId",
      "driverId",
      "rideMode",
      "privateMileage",
      "startAddress",
      "endAddress",
      "comments"
    ];

    return fieldsToCompare.some((field) => {
      const currentValue = values[field];
      const initialValue = initialValues[field];

      if (field === "rideMode" || field === "privateMileage") {
        const current = currentValue ? Number(currentValue) : 0;
        const initial = initialValue ? Number(initialValue) : 0;
        return current !== initial;
      }

      const current = currentValue || "";
      const initial = initialValue || "";
      return current !== initial;
    });
  }, [values, initialValues]);

  const debounceFetchPlaces = (searchedText: any) => {
    dispatch(getGooglePlacesThunk(searchedText));
  };

  useEffect(() => {
    dispatch(getAllKeysByAccountThunk(accountId ?? ""));
    dispatch(getAccountPointsOfInterestsThunk(accountId ?? ""));
    dispatch(getUsersBasicInfoThunk(accountId ?? ""));
    dispatch(getSpecificSettingThunk());
    dispatch(getAllUserRolesThunk());
  }, [accountId]);

  useEffect(() => {
    if (accountId !== null && (specificSetting as any)?.uniqueId) {
      dispatch(
        getSpecificAccountUserThunk({
          accountId: accountId,
          userId: (specificSetting as any).uniqueId
        })
      );
    }
  }, [accountId, (specificSetting as any)?.uniqueId]);

  useEffect(() => {
    // @ts-ignore
    const privateMileage = Number(values.privateMileage) || 0;

    if (privateMileage > 0) {
      if (values.rideMode !== 4) {
        setFieldValue("rideMode", 4);
      }
    } else {
      if (values.rideMode === 4) {
        setFieldValue("rideMode", 1);
      }
    }

    if (
      objectRide?.mileage &&
      typeof objectRide.mileage === "number" &&
      objectRide.mileage > 0 &&
      privateMileage > objectRide.mileage
    ) {
      const cleanMaxMileage = Math.round(objectRide.mileage * 100) / 100;
      setFieldValue("privateMileage", cleanMaxMileage);
    }
    // @ts-ignore
  }, [objectRide, values.privateMileage, values.rideMode]);

  const onCloseHandler = () => {
    // @ts-ignore
    onClose();
    resetForm();
  };

  return (
    <Modal
      open={open}
      onClose={onCloseHandler}
      title={t("updateObjectRideForm.title")}
      primaryBtnProps={{
        type: "submit",
        text: t("buttonsText.saveChanges"),
        loading: isSubmitting || customIsSubmitting,
        disabled: !isValid || !hasChanges,
        // @ts-ignore
        onClick: handleSubmit
      }}
      secondaryBtnProps={{
        text: t("buttonsText.cancel"),
        onClick: onCloseHandler
      }}
      theme={theme}
      {...rest}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "space-between"
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#39169E",
              textTransform: "uppercase",
              width: "100%"
            }}
          >
            {t("updateObjectRideForm.generalInformation")}
          </Typography>
          <Divider
            orientation="horizontal"
            flexItem
            sx={{ width: "50%", marginBottom: "12px" }}
          />
        </Box>
        <Stack spacing={2}>
          <FormikSelectField
            // @ts-ignore
            key={`keyId-${values.driverId || "empty"}`}
            theme={theme}
            label={t("updateObjectRideForm.key")}
            name="keyId"
            options={accountKeys ?? []}
            optionLabelKey={"label"}
            optionValueKey={"uniqueId"}
            loading={accountKeysLoading}
            searchable
            fullWidth
            onChange={() => {
              setFieldValue("driverId", "");
            }}
            onClearText={() => {
              setFieldValue("keyId", "");
            }}
            disabled={!ALLOWED_ROLES.includes(accountRole)}
          />
          <FormikSelectField
            key={`driverId-${values.keyId || "empty"}`}
            theme={theme}
            label={t("driver.label", {
              ns: "formFieldsTranslation"
            })}
            placeholder={t("driver.placeholder", {
              ns: "formFieldsTranslation"
            })}
            name="driverId"
            options={usersBasicInfo ?? []}
            optionLabelKey="email"
            optionValueKey="uniqueId"
            searchable
            fullWidth
            onChange={() => {
              setFieldValue("keyId", "");
            }}
            onClearText={() => {
              setFieldValue("driverId", "");
            }}
            disabled={!ALLOWED_ROLES.includes(accountRole)}
          />
          {
            <FormikSelectField
              theme={theme}
              label={t("updateObjectRideForm.type")}
              name="rideMode"
              options={rideModes ?? []}
              optionLabelKey={"label"}
              optionValueKey={"value"}
              required
              fullWidth
              // @ts-ignore
              disabled={(values?.privateMileage ?? 0) > 0}
            />
          }

          <Stack spacing={2}>
            {objectRide &&
              !objectRide.startAddress &&
              !objectRide?.correctedStartAddress && (
                <FormikInputField
                  theme={theme}
                  label={t("updateObjectRideForm.rideStartAddress")}
                  placeholder="Address"
                  name="startAddress"
                  fullWidth
                />
              )}

            {objectRide &&
              !objectRide.endAddress &&
              !objectRide?.correctedEndAddress && (
                <FormikInputField
                  theme={theme}
                  label={t("updateObjectRideForm.rideEndAddress")}
                  placeholder="Address"
                  name="endAddress"
                  fullWidth
                />
              )}
          </Stack>

          {Number(values.rideMode) !== 2 && (
            <FormikInputField
              theme={theme}
              label={t("updateObjectRideForm.privateMileageKm")}
              name="privateMileage"
              type="number"
              inputProps={{
                max: objectRide?.mileage,
                min: 0,
                step: "any"
              }}
              fullWidth
              disabled={values.rideMode == 2}
              helperText={
                objectRide?.mileage
                  ? `Maximum: ${objectRide.mileage} km`
                  : undefined
              }
            />
          )}
          <FormikInputField
            theme={theme}
            label={t("updateObjectRideForm.comment")}
            required={false}
            name="comments"
            multiline
            rows={2}
            fullWidth
            placeholder="Add any additional comments"
          />
          <FormikInputField
            theme={theme}
            label={t("changeReason.label", { ns: "formFieldsTranslation" })}
            required
            name="changeReason"
            fullWidth
            placeholder={t("changeReason.placeholder", {
              ns: "formFieldsTranslation"
            })}
            multiline
            rows={2}
          />
        </Stack>
      </Stack>
    </Modal>
  );
};

export default InnerForm;
